import React, { useState } from 'react'
import {Rocket, AlertCircle, CheckCircle2, ExternalLink, Loader2} from 'lucide-react'

export function TokenLauncher() {
  const [tokenName, setTokenName] = useState('')
  const [tokenSymbol, setTokenSymbol] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [tweetUrl, setTweetUrl] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [feeRecipientType, setFeeRecipientType] = useState<'wallet' | 'x' | 'farcaster' | 'ens'>('wallet')
  const [feeRecipientValue, setFeeRecipientValue] = useState('')
  const [simulateOnly, setSimulateOnly] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState('')

  const handleLaunch = async () => {
    if (!tokenName || !feeRecipientValue) {
      setError('Token name and fee recipient are required')
      return
    }

    setLoading(true)
    setError('')
    setResult(null)

    try {
      const response = await fetch('https://api.lumi.new/v1/functions/p411756259739451392/launchToken', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tokenName,
          tokenSymbol: tokenSymbol || undefined,
          description: description || undefined,
          image: imageUrl || undefined,
          tweetUrl: tweetUrl || undefined,
          websiteUrl: websiteUrl || undefined,
          feeRecipient: {
            type: feeRecipientType,
            value: feeRecipientValue
          },
          simulateOnly
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to launch token')
      }

      setResult(data)
    } catch (err: any) {
      setError(err.message || 'An error occurred while launching the token')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setTokenName('')
    setTokenSymbol('')
    setDescription('')
    setImageUrl('')
    setTweetUrl('')
    setWebsiteUrl('')
    setFeeRecipientType('wallet')
    setFeeRecipientValue('')
    setSimulateOnly(false)
    setResult(null)
    setError('')
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <img 
              src="https://static.lumi.new/8c/8c6701c8121bc663da0f59e83a67f7e6.webp" 
              alt="Token Launcher" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Token Launcher</h1>
          <p className="text-gray-400 text-lg">
            Deploy tokens on Base via Bankr Bot with automated fee distribution
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-electric-purple-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="mb-2">
                <strong className="text-white">Powered by Bankr Bot Partner API</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-400">
                <li>Deploy tokens on Base blockchain instantly</li>
                <li>Automatic Uniswap v4 pool creation with bonding curve</li>
                <li>1.2% swap fee distributed among creator, platform, and protocol</li>
                <li>Creator receives 57% of all trading fees</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Launch Form */}
        <div className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Launch Your Token</h2>

          {/* Token Name */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Token Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              placeholder="My Token"
              maxLength={100}
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">1-100 characters</p>
          </div>

          {/* Token Symbol */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Token Symbol (Ticker)
            </label>
            <input
              type="text"
              value={tokenSymbol}
              onChange={(e) => setTokenSymbol(e.target.value.toUpperCase())}
              placeholder="MTK (defaults to first 4 chars of name)"
              maxLength={10}
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">1-10 characters, optional</p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description of your token..."
              maxLength={500}
              rows={3}
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Max 500 characters, stored in on-chain metadata</p>
          </div>

          {/* Image URL */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Token Logo URL
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Uploaded to IPFS as part of token metadata</p>
          </div>

          {/* Tweet URL */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Tweet URL
            </label>
            <input
              type="url"
              value={tweetUrl}
              onChange={(e) => setTweetUrl(e.target.value)}
              placeholder="https://x.com/user/status/123456"
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Link to a tweet about your token</p>
          </div>

          {/* Website URL */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Website URL
            </label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://mytoken.xyz"
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Your token's website</p>
          </div>

          {/* Fee Recipient */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Fee Recipient <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              {(['wallet', 'x', 'farcaster', 'ens'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFeeRecipientType(type)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    feeRecipientType === type
                      ? 'bg-electric-purple-500 text-white'
                      : 'bg-deep-purple-950/30 text-gray-400 hover:text-white border border-deep-purple-800'
                  }`}
                >
                  {type === 'wallet' ? 'Wallet' : type === 'x' ? 'X/Twitter' : type === 'farcaster' ? 'Farcaster' : 'ENS'}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={feeRecipientValue}
              onChange={(e) => setFeeRecipientValue(e.target.value)}
              placeholder={
                feeRecipientType === 'wallet' 
                  ? '0x87be4dA49869fD055d5a60cAc2a6Dc61fdd3052D'
                  : feeRecipientType === 'x'
                  ? '@username'
                  : feeRecipientType === 'farcaster'
                  ? 'username.eth'
                  : 'vitalik.eth'
              }
              className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Who receives the creator's 57% share of trading fees
            </p>
          </div>

          {/* Simulate Only */}
          <div className="mb-8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={simulateOnly}
                onChange={(e) => setSimulateOnly(e.target.checked)}
                className="w-5 h-5 rounded border-deep-purple-800 bg-deep-purple-950/30 text-electric-purple-500 focus:ring-2 focus:ring-electric-purple-500"
              />
              <div>
                <span className="text-white font-medium">Simulate Only</span>
                <p className="text-xs text-gray-500">Test without broadcasting the transaction</p>
              </div>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <div>
                  <h3 className="text-white font-bold text-lg mb-2">
                    {simulateOnly ? 'Simulation Successful!' : 'Token Deployed Successfully!'}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    {simulateOnly ? 'Your token deployment has been simulated.' : 'Your token has been deployed to Base blockchain.'}
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                  <span className="text-gray-400">Token Address:</span>
                  <span className="text-white font-mono">{result.tokenAddress?.slice(0, 10)}...{result.tokenAddress?.slice(-8)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                  <span className="text-gray-400">Pool ID:</span>
                  <span className="text-white font-mono">{result.poolId?.slice(0, 10)}...{result.poolId?.slice(-8)}</span>
                </div>
                {result.txHash && (
                  <div className="flex justify-between items-center py-2 border-b border-green-500/20">
                    <span className="text-gray-400">Transaction:</span>
                    <a
                      href={`https://basescan.org/tx/${result.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-electric-purple-400 hover:text-electric-purple-300 flex items-center gap-1"
                    >
                      View on BaseScan <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400">Chain:</span>
                  <span className="text-white uppercase">{result.chain}</span>
                </div>
              </div>

              {result.feeDistribution && (
                <div className="mt-4 pt-4 border-t border-green-500/20">
                  <h4 className="text-white font-semibold mb-3">Fee Distribution (1.2% swap fee)</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Creator (You):</span>
                      <span className="text-green-400 font-semibold">{(result.feeDistribution.creator.bps / 100).toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Partner:</span>
                      <span className="text-gray-300">{(result.feeDistribution.partner.bps / 100).toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Bankr:</span>
                      <span className="text-gray-300">{(result.feeDistribution.bankr.bps / 100).toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Protocol:</span>
                      <span className="text-gray-300">{(result.feeDistribution.protocol.bps / 100).toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={resetForm}
                className="w-full mt-6 bg-deep-purple-950/50 hover:bg-deep-purple-950/70 text-white py-3 rounded-lg font-medium transition-all"
              >
                Launch Another Token
              </button>
            </div>
          )}

          {/* Launch Button */}
          {!result && (
            <button
              onClick={handleLaunch}
              disabled={loading || !tokenName || !feeRecipientValue}
              className="w-full bg-gradient-to-r from-electric-purple-500 to-purple-600 hover:from-electric-purple-600 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold text-lg transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {simulateOnly ? 'Simulating...' : 'Launching...'}
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  {simulateOnly ? 'Simulate Launch' : 'Launch Token'}
                </>
              )}
            </button>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Powered by <a href="https://bankr.bot" target="_blank" rel="noopener noreferrer" className="text-electric-purple-400 hover:text-electric-purple-300">Bankr Bot</a></p>
          <p className="mt-2">Tokens deployed on Base blockchain • Trading fees distributed automatically</p>
        </div>
      </div>
    </div>
  )
}
